// src/utils/jobStatusApi.ts

import axiosInstance from "../actions/utils/axiosInstance";
import { telemetryClient } from "../services/telemetryClient";

const DEFAULT_RATE_LIMIT_BACKOFF_MS = 10_000;
const MAX_RATE_LIMIT_RETRIES = 1;

const inFlightJobStatusRequests = new Map<string, Promise<JobStatusResponse | null>>();
const jobStatusBackoffUntilMs = new Map<string, number>();
const activeJobStatusPollers = new Map<string, string>();

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getJobStatusKey(jobId: string): string {
  return jobId.trim();
}

function getRetryAfterMs(error: any): number {
  const retryAfterHeader = error?.response?.headers?.["retry-after"];
  const retryAfterBody = error?.response?.data?.retryAfter;
  const rawRetryAfter = retryAfterHeader ?? retryAfterBody;

  if (typeof rawRetryAfter === "number" && Number.isFinite(rawRetryAfter) && rawRetryAfter > 0) {
    return rawRetryAfter * 1000;
  }

  if (typeof rawRetryAfter === "string") {
    const retryAfterSeconds = Number(rawRetryAfter);
    if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
      return retryAfterSeconds * 1000;
    }
  }

  return DEFAULT_RATE_LIMIT_BACKOFF_MS;
}

async function waitForBackoffWindow(jobId: string): Promise<void> {
  const backoffUntil = jobStatusBackoffUntilMs.get(jobId);
  if (!backoffUntil) {
    return;
  }

  const remainingMs = backoffUntil - Date.now();
  if (remainingMs <= 0) {
    jobStatusBackoffUntilMs.delete(jobId);
    return;
  }

  console.warn(`Waiting for job status rate-limit backoff: ${jobId}`, { remainingMs });
  await sleep(remainingMs);
}

function normalizeJobStatusResponse(data: any): JobStatusResponse | null {
  if (!data || !data.jobId) {
    console.error("Invalid response from universal status endpoint:", data);
    return null;
  }

  return {
    jobId: data.jobId,
    jobType: data.jobType,
    status: data.status,
    isCompleted: data.isCompleted,
    isError: data.isError,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    stepHistory: data.stepHistory || [],
    currentStep: data.currentStep,
    progress: data.progress || 0,
    outputLocation: data.outputLocation,
    errorMessage: data.errorMessage,
    metadata: data.metadata || {},
  };
}

async function fetchJobStatusWithBackoff(jobId: string, attempt = 0): Promise<JobStatusResponse | null> {
  await waitForBackoffWindow(jobId);

  try {
    console.debug(`Fetching universal job status: ${jobId}`);

    const response = await axiosInstance.get("/api/jobs/status", {
      params: { jobId },
    });

    jobStatusBackoffUntilMs.delete(jobId);

    const jobStatus = normalizeJobStatusResponse(response.data);
    if (!jobStatus) {
      return null;
    }

    console.debug(`Job status retrieved: ${jobId}`, {
      status: jobStatus.status,
      jobType: jobStatus.jobType,
      stepCount: jobStatus.stepHistory.length,
      progress: jobStatus.progress,
    });

    return jobStatus;
  } catch (error: any) {
    if (error.response?.status === 404) {
      console.debug(`Job not found: ${jobId}`);
      return null;
    }

    if (error.response?.status === 429) {
      const retryAfterMs = getRetryAfterMs(error);
      const nextAllowedRequestAt = Date.now() + retryAfterMs;
      jobStatusBackoffUntilMs.set(jobId, nextAllowedRequestAt);

      console.warn(`Rate limited on job status check: ${jobId}`, {
        retryAfterMs,
        attempt,
      });

      telemetryClient.log("jobStatusApi.rateLimited", {
        jobId,
        level: "warn",
        metadata: { retryAfterMs, attempt },
      });

      if (attempt >= MAX_RATE_LIMIT_RETRIES) {
        return null;
      }

      await sleep(retryAfterMs);
      return fetchJobStatusWithBackoff(jobId, attempt + 1);
    }

    if (error.response?.status === 403) {
      console.warn(`Access denied to job: ${jobId}`);
      return null;
    }

    console.error(`Error fetching universal job status for ${jobId}:`, error);
    return null;
  }
}

/**
 * 🆕 UNIVERSAL JOB STATUS API
 * 
 * Simplified architecture using single universal endpoint:
 * - One endpoint: /api/jobs/status?jobId=<uuid>
 * - Works for ALL job types
 * - Complete step history included
 * - Registry-only backend (single source of truth)
 * - No more API spam from multiple endpoints
 */

/**
 * Individual step in job execution timeline
 */
export interface JobStepHistory {
  stepId: string;
  label: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  startTime: string;
  endTime?: string;
  progress?: number;
  metadata?: Record<string, any>;
}

/**
 * Universal job status response from centralized registry
 */
export interface JobStatusResponse {
  jobId: string;
  jobType: string; // memo, opinion, loss-adjuster-report, etc.
  status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  isCompleted: boolean;
  isError: boolean;
  createdAt: string;
  updatedAt: string;
  
  // 🆕 Complete execution timeline
  stepHistory: JobStepHistory[];
  currentStep?: string;
  progress: number; // 0-100
  
  // Output data
  outputLocation?: string;
  errorMessage?: string;
  
  // Domain-specific data (available in metadata)
  metadata: {
    // Memo-specific
    representedParty?: string;
    useThinking?: boolean;
    memoLength?: number;
    
    // Opinion-specific  
    caseType?: string;
    jurisdiction?: string;
    legalPrecedents?: string[];
    
    // Loss adjuster specific
    claimAmount?: number;
    assessmentType?: string;
    damageCategories?: string[];
    
    // Common processing metadata
    fileCount?: number;
    filesProcessed?: number;
    textLength?: number;
    userEmail?: string;
    firstName?: string;
    organizationId?: string;
    contentTextKey?: string;
    
    // Extensible for any domain-specific data
    [key: string]: any;
  };
}

/**
 * Supported job types (for reference only - no individual endpoints needed)
 */
export enum JobType {
  CLAIM = 'claim',
  MEMO = 'memo',
  MERITS_ASSESSMENT = 'merits-assessment',
  QUANTUM = 'quantum',
  EXPLANATION = 'explanation',
  THIRD_PARTY_RESPONSE = 'third-party-response',
  POLICY_ANALYSIS = 'policy-analysis',
  REPUDIATION_ASSESSMENT = 'repudiation-assessment',
  RECOVERY_MODEL = 'recovery-model',
  BROKER_RISK_ASSESSMENT = 'broker-risk-assessment',
  JUDGEMENT_SIMULATION = 'judgement-simulation',
  LOSS_ADJUSTER_REPORT = 'loss-adjuster-report',
  POLICY_QUOTE_COMPARISON = 'policy-quote-comparison',
  OMBUDSMAN_SIMULATION = 'ombudsman-simulation',
  RECORD_OF_ADVICE = 'record-of-advice',
  POLICY_WORDING_ANALYSIS = 'policy-wording-analysis',
  HPCSA_PI_RESPONSE = 'hpcsa-pi-response'
}

/**
 * 🆕 UNIVERSAL STATUS LOOKUP
 * 
 * Single function for ALL job types - replaces 17+ individual functions
 * 
 * @param jobId - The job ID to check (UUID format)
 * @returns Promise<JobStatusResponse | null> - Complete job data or null if not found
 */
export async function getJobStatus(jobId: string): Promise<JobStatusResponse | null> {
  if (!jobId || typeof jobId !== "string" || jobId.trim().length === 0) {
    console.error("Invalid jobId provided:", jobId);
    return null;
  }

  const normalizedJobId = getJobStatusKey(jobId);
  const existingRequest = inFlightJobStatusRequests.get(normalizedJobId);
  if (existingRequest) {
    telemetryClient.log("jobStatusApi.dedupHit", {
      jobId: normalizedJobId,
      metadata: { inFlightCount: inFlightJobStatusRequests.size },
    });
    return existingRequest;
  }

  const request = fetchJobStatusWithBackoff(normalizedJobId).finally(() => {
    inFlightJobStatusRequests.delete(normalizedJobId);
  });

  inFlightJobStatusRequests.set(normalizedJobId, request);
  return request;
}

export function tryAcquireJobStatusPoller(jobId: string, ownerId: string): boolean {
  const normalizedJobId = getJobStatusKey(jobId);
  const currentOwner = activeJobStatusPollers.get(normalizedJobId);

  if (!currentOwner || currentOwner === ownerId) {
    activeJobStatusPollers.set(normalizedJobId, ownerId);
    return true;
  }

  return false;
}

export function releaseJobStatusPoller(jobId: string, ownerId: string): void {
  const normalizedJobId = getJobStatusKey(jobId);
  const currentOwner = activeJobStatusPollers.get(normalizedJobId);

  if (currentOwner === ownerId) {
    activeJobStatusPollers.delete(normalizedJobId);
  }
}

export function resetJobStatusApiStateForTests(): void {
  inFlightJobStatusRequests.clear();
  jobStatusBackoffUntilMs.clear();
  activeJobStatusPollers.clear();
}

/**
 * 🆕 ALIAS: For backward compatibility
 * 
 * @deprecated Use getJobStatus() instead - this is just an alias now
 */
export const getCentralizedJobStatus = getJobStatus;

/**
 * 🚫 REMOVED: findJobStatus (caused API spam by looping through 17 endpoints)
 * 🚫 REMOVED: Individual job status functions (getMemoStatus, getOpinionStatus, etc.)
 * 🚫 REMOVED: Individual endpoint mappings (JOB_TYPE_ENDPOINTS)
 * 
 * ✅ REPLACED with single universal getJobStatus() function
 */

/**
 * Detects job type from URL path (for job creation routing)
 */
export function detectJobTypeFromPath(pathname: string): JobType | null {
  // Simplified path detection - just for UI routing, not API calls
  const pathMappings: Record<string, JobType> = {
    '/claim': JobType.CLAIM,
    '/memo': JobType.MEMO,
    '/merits-assessment-2': JobType.MERITS_ASSESSMENT,
    '/quantum': JobType.QUANTUM,
    '/explanation': JobType.EXPLANATION,
    '/third-party-response': JobType.THIRD_PARTY_RESPONSE,
    '/policy-analysis': JobType.POLICY_ANALYSIS,
    '/repudiation-assessment': JobType.REPUDIATION_ASSESSMENT,
    '/recovery-model': JobType.RECOVERY_MODEL,
    '/broker-risk-assessment': JobType.BROKER_RISK_ASSESSMENT,
    '/judgement-simulation': JobType.JUDGEMENT_SIMULATION,
    '/loss-adjuster-report': JobType.LOSS_ADJUSTER_REPORT,
    '/policy-quote-comparison': JobType.POLICY_QUOTE_COMPARISON,
    '/ombudsman-simulation': JobType.OMBUDSMAN_SIMULATION,
    '/record-of-advice': JobType.RECORD_OF_ADVICE,
    '/policy-wording-analysis': JobType.POLICY_WORDING_ANALYSIS,
    '/hpcsa-pi-response': JobType.HPCSA_PI_RESPONSE
  };
  
  for (const [pattern, jobType] of Object.entries(pathMappings)) {
    if (pathname.includes(pattern)) {
      return jobType;
    }
  }
  
  return null;
}

/**
 * 🆕 UTILITY: Get domain-specific data from metadata
 * 
 * Examples:
 * - getMemoData(job) => { representedParty, useThinking, memoLength }
 * - getMeritsAssessmentData(job) => { caseType, jurisdiction, legalPrecedents }
 */
export function getMemoData(job: JobStatusResponse) {
  return {
    representedParty: job.metadata.representedParty,
    useThinking: job.metadata.useThinking,
    memoLength: job.metadata.memoLength
  };
}

export function getMeritsAssessmentData(job: JobStatusResponse) {
  return {
    caseType: job.metadata.caseType,
    jurisdiction: job.metadata.jurisdiction,
    legalPrecedents: job.metadata.legalPrecedents
  };
}

export function getLossAdjusterData(job: JobStatusResponse) {
  return {
    claimAmount: job.metadata.claimAmount,
    assessmentType: job.metadata.assessmentType,
    damageCategories: job.metadata.damageCategories
  };
} 